import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { getCltvSectionDefinition } from "./CltvDefinitions";
import {
  CltvSectionTitle,
  FloatingTooltip,
  SpendingTooltip,
} from "./CltvDashboardShared";
import "../Styles/CltvActualVsPredictedRevenue.scss";

export type SpendingChartPoint = {
  label: string;
  fullLabel: string;
  predicted: number;
  actual: number | null;
  predictedRaw: number | null;
  actualRaw: number | null;
  predictedFormattedValue?: string;
  actualFormattedValue?: string;
  bandLower: number;
  bandUpper: number;
  status: string;
};

type CltvActualVsPredictedRevenueProps = {
  loading: boolean;
  hasLoadedSpendingPatterns: boolean;
  chartsError: string | null;
  spendingPatternsLength: number;
  spendingChartData: SpendingChartPoint[];
  spendingYAxisDomain: [number, number];
  spendingSubtitle: string;
  formatKLabel: (_value: number) => string;
  formatSpendingBucket: (_raw: string) => string;
  formatMoney: (_value: number) => string;
};

const CltvActualVsPredictedRevenue: FC<CltvActualVsPredictedRevenueProps> = ({
  loading,
  hasLoadedSpendingPatterns,
  chartsError,
  spendingPatternsLength,
  spendingChartData,
  spendingYAxisDomain,
  spendingSubtitle,
  formatKLabel,
  formatSpendingBucket,
  formatMoney,
}) => {
  const { t } = useTranslation();
  const [spendingHover, setSpendingHover] = useState<{ x: number; y: number; index: number } | null>(
    null,
  );
  const handleSpendingChartMouseMove = useCallback((state: unknown, event: unknown) => {
    const chartState = state as {
      isTooltipActive?: boolean;
      activeTooltipIndex?: number;
    } | null;
    // Recharts' own `activeCoordinate` is relative to the chart's SVG, which needs
    // re-deriving the chart's on-screen position to place a viewport-fixed tooltip —
    // fragile the moment that container isn't quite what it's assumed to be (e.g. a
    // sidebar overlapping the page rather than sitting beside it). The native mouse
    // event's `clientX`/`clientY` are already real viewport pixels, no conversion needed.
    const mouseEvent = event as { clientX?: number; clientY?: number } | null;
    if (
      chartState?.isTooltipActive &&
      typeof chartState.activeTooltipIndex === "number" &&
      typeof mouseEvent?.clientX === "number" &&
      typeof mouseEvent?.clientY === "number"
    ) {
      setSpendingHover({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        index: chartState.activeTooltipIndex,
      });
    }
  }, []);
  const handleSpendingChartMouseLeave = useCallback(() => setSpendingHover(null), []);

  return (
    <article className="cltv-dashboard__panel cltv-dashboard__panel--spending" data-pdf-block="spending-chart">
      <div className="cltv-dashboard__spending-header">
        <div>
          <CltvSectionTitle
            title={t("cltv.sections.spendingPatterns")}
            tooltip={getCltvSectionDefinition("spendingPatterns", t)}
          />
        </div>
        {/* Own full-width rows of the header flex, so they span the panel
            instead of being squeezed into the column beside the legend. */}
        <p className="cltv-dashboard__panel-note">
          {t("cltv.notes.predictionAccuracy")}
        </p>
        <div className="cltv-dashboard__spending-subtitle-row">
          <p className="cltv-dashboard__chart-subtitle">{spendingSubtitle}</p>
          <div className="cltv-dashboard__spending-legend">
            <span>
              <i className="is-actual" /> {t("cltv.legend.actualRevenue")}
            </span>
            <span>
              <i className="is-predicted" /> {t("cltv.legend.predictedRevenue")}
            </span>
          </div>
        </div>
      </div>
      {loading && !hasLoadedSpendingPatterns ? (
        <LoadingSpinner compact />
      ) : chartsError && spendingPatternsLength === 0 ? (
        <p className="cltv-dashboard__empty" role="alert">
          {chartsError}
        </p>
      ) : (
        <div className="cltv-dashboard__chart">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={spendingChartData}
              margin={{ top: 28, right: 20, left: 10, bottom: 10 }}
              onMouseMove={handleSpendingChartMouseMove}
              onMouseLeave={handleSpendingChartMouseLeave}
            >
              <CartesianGrid stroke="#e8eef8" strokeDasharray="3 4" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: "#8aa0bf" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, spendingYAxisDomain[1]]}
                tickFormatter={formatKLabel}
                tick={{ fontSize: 12, fill: "#8aa0bf" }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <ReferenceLine
                x={spendingChartData[3]?.label}
                stroke="#8aa0bf"
                strokeDasharray="6 4"
                strokeWidth={1.5}
                label={{
                  value: t("cltv.charts.currentMonth"),
                  position: "top",
                  fill: "#587194",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              />
              <Tooltip cursor={{ stroke: "#c6d3e6", strokeWidth: 1 }} content={() => null} />
              <Line
                type="linear"
                dataKey="predicted"
                name="predicted"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="6 4"
                strokeLinecap="round"
                dot={false}
                activeDot={{ r: 5, fill: "#3b82f6", stroke: "#ffffff", strokeWidth: 2 }}
              />
              <Line
                type="linear"
                dataKey="actual"
                name="actual"
                stroke="#101828"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={{ r: 4, fill: "#101828", stroke: "#ffffff", strokeWidth: 2 }}
                connectNulls={false}
                activeDot={{ r: 6, fill: "#101828", stroke: "#ffffff", strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          {spendingHover && spendingChartData[spendingHover.index] ? (
            <FloatingTooltip
              x={spendingHover.x}
              y={spendingHover.y}
              contentKey={spendingHover.index}
            >
              <SpendingTooltip
                active
                label={spendingChartData[spendingHover.index].label}
                payload={[{ value: 0, payload: spendingChartData[spendingHover.index] }]}
                formatBucket={formatSpendingBucket}
                formatMoney={formatMoney}
              />
            </FloatingTooltip>
          ) : null}
        </div>
      )}
    </article>
  );
};

export default CltvActualVsPredictedRevenue;
